/**
 * SM-2-variant spaced-repetition scheduler.
 *
 * Pure functions only: `now` (epoch ms) and `rand` (a `[0, 1)` source) are
 * always injected by the caller — never `Date.now()` or `Math.random()`
 * inside this module. Inputs are never mutated; every function returns a
 * new {@link ReviewState}.
 */

const MIN_EASE = 1.3;
const MAX_EASE = 3.0;
const DAY_MS = 86_400_000;
const AGAIN_DELAY_MS = 10 * 60_000;

/** Recall grade for a single review: Again / Hard / Good / Easy. */
export type Grade = 0 | 1 | 2 | 3;

/** Per-item scheduling state tracked by the spaced-repetition engine. */
export interface ReviewState {
  itemId: string;
  /** Difficulty multiplier; always ≥1.3, starts at 2.5. */
  ease: number;
  /** Days until next review; 0 means the item is still in learning. */
  intervalDays: number;
  reps: number;
  lapses: number;
  /** Epoch ms at which the item next becomes due. */
  dueAt: number;
}

/** Create the initial state for a newly-introduced item, due immediately. */
export function introduce(itemId: string, now: number): ReviewState {
  return { itemId, ease: 2.5, intervalDays: 0, reps: 0, lapses: 0, dueAt: now };
}

/**
 * Apply fuzz to a computed interval and derive the resulting due timestamp.
 * Used for grades 1-3 only; Again (0) bypasses fuzz entirely.
 */
function withFuzz(
  intervalDays: number,
  now: number,
  rand: () => number,
): { intervalDays: number; dueAt: number } {
  const fuzzed = intervalDays * (0.95 + rand() * 0.1);
  return { intervalDays: fuzzed, dueAt: now + Math.round(fuzzed * DAY_MS) };
}

/** Score a single review, returning a new, updated {@link ReviewState}. */
export function review(
  state: ReviewState,
  grade: Grade,
  now: number,
  rand: () => number,
): ReviewState {
  if (grade === 0) {
    return {
      ...state,
      ease: Math.max(MIN_EASE, state.ease - 0.2),
      intervalDays: 0,
      reps: 0,
      lapses: state.lapses + 1,
      dueAt: now + AGAIN_DELAY_MS,
    };
  }

  if (grade === 1) {
    const { intervalDays, dueAt } = withFuzz(
      Math.max(1, state.intervalDays * 1.2),
      now,
      rand,
    );
    return {
      ...state,
      ease: Math.max(MIN_EASE, state.ease - 0.15),
      intervalDays,
      reps: state.reps + 1,
      dueAt,
    };
  }

  if (grade === 2) {
    const base = state.intervalDays < 1 ? 1 : state.intervalDays * state.ease;
    const { intervalDays, dueAt } = withFuzz(base, now, rand);
    return { ...state, intervalDays, reps: state.reps + 1, dueAt };
  }

  const base = Math.max(
    2,
    (state.intervalDays < 1 ? 1 : state.intervalDays) * state.ease * 1.3,
  );
  const { intervalDays, dueAt } = withFuzz(base, now, rand);
  return {
    ...state,
    ease: Math.min(MAX_EASE, state.ease + 0.15),
    intervalDays,
    reps: state.reps + 1,
    dueAt,
  };
}

/** Whether an item is due for review at the given time (inclusive). */
export function isDue(state: ReviewState, now: number): boolean {
  return state.dueAt <= now;
}
